import { FailedToGetData, FloatingSaveBar, Hr, Loading } from 'src/components/_commons';
import { FC, useEffect, useMemo, useState } from 'react';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { Meeting } from '../../_commons/Meeting';
import { meetingServices } from 'src/services';
import { typeMeeting, typeMeetingsDoc, typeUser } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetIsDarkMode, useGetShowingLoadingModal } from 'src/custom-hooks';

type propsType = {
    closeCrud: () => void;
    meetingsDoc: typeMeetingsDoc;
    meetingToEdit: typeMeeting | null;
    users: typeUser[];
    weekNumber: number;
    year: number;
};

export const MidweekMeetingsCrud: FC<propsType> = ({ closeCrud, meetingsDoc, meetingToEdit, users, weekNumber, year }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [meeting, setMeeting] = useState<typeMeeting | null>(meetingToEdit ?? null);
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();
    const isEdit = !!meetingToEdit;
    const showingLoadingModal = useGetShowingLoadingModal();

    const isValid: boolean = useMemo(() =>
        !!meeting
        && !!meeting.date
    , [meeting]);

    const cancelHandler = () => {
        dispatch(setValuesAndOpenAlertModalReducer({
            mode: 'confirm',
            title: 'Confirmar',
            message: `Se va a cancelar la ${isEdit ? 'edición' : 'creación'} de la reunión de la semana ${weekNumber} de ${year}`,
            animation: 3,
            execution: closeCrud
        }));
    }

    const saveHandler = () => {
        if (!isValid) {
            return;
        }
        dispatch(setValuesAndOpenAlertModalReducer({
            mode: 'confirm',
            title: 'Confirmar',
            message: `Se va a ${isEdit ? 'editar' : 'crear'} la reunión de la semana ${weekNumber} de ${year}`,
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await meetingServices.SaveMidweek(meeting!);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                closeCrud();
            }
        }));
    }

    const deleteHandler = () => {
        dispatch(setValuesAndOpenAlertModalReducer({
            title: 'Eliminar',
            message: 'Se va a eliminar esta reunión junto con todas sus asignaciones. Este paso es necesario en caso de que la reunión sea actualizada en el sitio fuente (WOL).',
            animation: 3,
            mode: 'confirm',
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await meetingServices.DeleteMidweek(meeting!);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                closeCrud();
            }
        }));
    }

    useEffect(() => {
        if (isEdit && meetingToEdit.sections.length) {
            setIsLoading(false);
            return;
        }
        meetingServices.GetNewMidweek(weekNumber, year).then(response => {
            setIsLoading(false);
            if (!response.success || !response.data?.midweekMeeting) {
                dispatch(setAlertResponse({ response }));
                closeCrud();
                return;
            }
            setMeeting(response.data.midweekMeeting);
        });
    }, []);

    if (isLoading) {
        return <Loading mt={'40px'} />;
    }

    if (!meeting || !users) {
        return <FailedToGetData />;
    }

    return (
        <div className={`${isDarkMode ? 'bg-lightdark text-white' : ''} mt-5`}>
            <Meeting
                meeting={meeting}
                meetingsDoc={meetingsDoc}
                readOnly={false}
                setMeeting={setMeeting}
                type={'midweek'}
                users={users}
                weekNumber={weekNumber}
                year={year}
            />
            <FloatingSaveBar
                cancelHandler={cancelHandler}
                isDisabledSaveButton={!isValid}
                saveHandler={saveHandler}
            />

            {isEdit &&
                <>
                <Hr classes={'my-5'} />
                <div className={'d-flex justify-content-center gap-2 pb-5 mt-5'}>
                    <button className={'btn btn-yellow border text-white'}
                        style={{ width: '150px' }}
                        onClick={deleteHandler}
                        disabled={showingLoadingModal}
                    >
                        Eliminar Reunión
                    </button>
                </div>
                </>
            }
        </div>
    );
}
